import React, { useEffect, useMemo, useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import PropertyCard from "../components/PropertyCard";
import { fetchProperties } from "../lib/api";

const LOCATIONS = [
  "Marbella",
  "Estepona",
  "Sotogrande",
  "Benahavís",
  "Casares",
  "Torremolinos",
  "Mijas",
  "Fuengirola",
  "Benalmádena",
];

const TYPES = [
  { value: "1-1", label: "Apartment" },
  { value: "2-1", label: "House" },
  { value: "2-4", label: "Villa" },
  { value: "1-3", label: "Penthouse" },
  { value: "1-5", label: "Townhouse" },
  { value: "3-1", label: "Plot" },
];

export default function Properties() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [queryInfo, setQueryInfo] = useState({});
  const [page, setPage] = useState(1);

  const [filters, setFilters] = useState({
    location: "",
    type: "",
    bedrooms: "",
    min_price: "",
    max_price: "",
  });
  const [applied, setApplied] = useState(filters);

  const params = useMemo(() => {
    const p = { page, per_page: 12 };
    if (applied.location) p.location = applied.location;
    if (applied.type) p.type = applied.type;
    if (applied.bedrooms) p.bedrooms = applied.bedrooms;
    if (applied.min_price) p.min_price = applied.min_price;
    if (applied.max_price) p.max_price = applied.max_price;
    return p;
  }, [applied, page]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    fetchProperties(params)
      .then((d) => {
        if (!mounted) return;
        if (d.ok && d.properties && d.properties.length > 0) {
          setItems(d.properties);
          setQueryInfo(d.query_info || {});
        } else {
          // Live API returned no results — show empty state, NEVER mock listings
          setItems([]);
          setQueryInfo({ PropertyCount: 0, CurrentPage: 1, PropertiesPerPage: 0 });
        }
      })
      .catch((e) => mounted && setError(e?.message || "Error"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [params]);

  const apply = (e) => {
    e?.preventDefault();
    setPage(1);
    setApplied(filters);
  };

  const reset = () => {
    const empty = { location: "", type: "", bedrooms: "", min_price: "", max_price: "" };
    setFilters(empty);
    setApplied(empty);
    setPage(1);
  };

  return (
    <div className="bg-charcoal pt-32 pb-24" data-testid="page-properties">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        <div className="mb-14">
          <p className="overline">{t.properties.overline}</p>
          <h1 className="font-serif text-4xl md:text-6xl text-cream leading-[1.05] mt-4 max-w-3xl">
            {t.properties.title}
          </h1>
          <span className="divider-gold mt-6" />
        </div>

        {/* Filters */}
        <form
          onSubmit={apply}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 border border-white/10 p-5 mb-12"
          data-testid="property-filters"
        >
          <select
            value={filters.location}
            onChange={(e) => setFilters((f) => ({ ...f, location: e.target.value }))}
            className="input-luxury"
            data-testid="filter-location"
          >
            <option value="">{t.properties.anyLocation}</option>
            {LOCATIONS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <select
            value={filters.type}
            onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
            className="input-luxury"
            data-testid="filter-type"
          >
            <option value="">{t.properties.anyType}</option>
            {TYPES.map((tt) => (
              <option key={tt.value} value={tt.value}>
                {tt.label}
              </option>
            ))}
          </select>
          <select
            value={filters.bedrooms}
            onChange={(e) => setFilters((f) => ({ ...f, bedrooms: e.target.value }))}
            className="input-luxury"
            data-testid="filter-bedrooms"
          >
            <option value="">{t.properties.any}</option>
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>
                {n}+ {t.properties.bedsLabel}
              </option>
            ))}
          </select>
          <input
            type="number"
            placeholder={t.properties.minPrice}
            value={filters.min_price}
            onChange={(e) => setFilters((f) => ({ ...f, min_price: e.target.value }))}
            className="input-luxury"
            data-testid="filter-min-price"
          />
          <input
            type="number"
            placeholder={t.properties.maxPrice}
            value={filters.max_price}
            onChange={(e) => setFilters((f) => ({ ...f, max_price: e.target.value }))}
            className="input-luxury"
            data-testid="filter-max-price"
          />
          <div className="flex gap-2">
            <button type="submit" className="btn-gold-solid flex-1" data-testid="filter-apply">
              {t.properties.apply}
            </button>
            <button
              type="button"
              onClick={reset}
              className="btn-gold-outline px-3"
              data-testid="filter-reset"
            >
              {t.properties.reset}
            </button>
          </div>
        </form>

        {/* Status */}
        <div className="flex items-center justify-between mb-8 text-cream-muted text-xs tracking-[0.18em] uppercase">
          <span>
            {loading
              ? t.properties.loading
              : `${queryInfo.PropertyCount || items.length} ${t.properties.total}`}
          </span>
          {queryInfo.CurrentPage && (
            <span data-testid="pagination-info">
              {queryInfo.CurrentPage} / {Math.max(1, Math.ceil((queryInfo.PropertyCount || 0) / (queryInfo.PropertiesPerPage || 12)))}
            </span>
          )}
        </div>

        {/* List */}
        {error && (
          <div className="border border-gold/30 p-6 text-cream-muted text-sm mb-8" data-testid="properties-error">
            {t.properties.noResults}
          </div>
        )}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="aspect-[4/3] bg-charcoal-surface animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center text-cream-muted" data-testid="properties-empty">
            {t.properties.noResults}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10" data-testid="properties-grid">
            {items.map((p, i) => (
              <PropertyCard property={p} key={p.reference} index={i} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {queryInfo.PropertyCount > (queryInfo.PropertiesPerPage || 12) && (
          <div className="flex justify-center gap-3 mt-14">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn-gold-outline px-5 disabled:opacity-40"
              data-testid="page-prev"
            >
              ← Prev
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              className="btn-gold-outline px-5"
              data-testid="page-next"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
